import { useState } from "preact/compat"

import { IntegrationWithJobInfo, SageIntacctConfig, updateIntegration } from "@/api/integration"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"

interface SageSyncSettingsProps {
  integration: IntegrationWithJobInfo
}

/**
 * Controls how far back the Sage invoice/payment sync reaches. Sage's API is metered per call, so
 * the first sync is bounded — two months back by default — and this start date widens (or narrows)
 * the window. Later syncs are incremental from the last run regardless.
 */
export function SageSyncSettings({ integration }: SageSyncSettingsProps) {
  const config = integration.data as SageIntacctConfig
  const [start_date, setStartDate] = useState<string>(config.sync_start_date ?? "")
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    // An empty string clears the setting — the settings merge ignores nulls.
    const res = await updateIntegration(integration.id, { data: { sync_start_date: start_date } })
    setSaving(false)
    if (!res.ok) {
      toast({ title: "Save failed", description: "Could not save the sync window.", variant: "destructive" })
      return
    }
    toast({
      title: "Settings saved",
      description: start_date
        ? `Invoices and payments will sync from ${start_date}.`
        : "Sync window reset to the default (2 months back).",
    })
  }

  return (
    <div class="space-y-3">
      <p class="text-sm text-gray-500">
        Sage Intacct's API is metered, so the first sync only fetches invoices and payments from the last two months. Set an
        earlier date here to pull more history (e.g. the start of your fiscal year); leave it blank for the default. Syncs after
        the first are incremental and unaffected.
      </p>
      <div class="space-y-1 max-w-md">
        <Label>Sync invoices &amp; payments since</Label>
        <Input type="date" value={start_date} onChange={(e: Event) => setStartDate((e.target as HTMLInputElement).value)} />
      </div>
      <Button variant="outline" disabled={saving} onClick={handleSave}>
        {saving ? "Saving…" : "Save sync window"}
      </Button>
    </div>
  )
}
